import { LANGUAGES, REASON_FIELDS, THEME_FIELDS } from "./catalogView"

// How the Setup tab tells one entry of GET /admin/tournament-feedback/catalog/history (sportya-api docs/tournament-feedback.md,
// „Istoricul”): the API sends each changed item before and after, and CP composes what changed from them.

const quoted = text => `„${text}”`
const itemText = item => item?.type === "reason" ? item.ro?.text : item?.ro?.title
const fieldsOf = item => item?.type === "reason" ? REASON_FIELDS : THEME_FIELDS
const FIELD_NAMES = { title: "title", reasonsLabel: "reasons label" }
const shown = text => typeof text === "string" && text ? text : "—"
const same = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null)

// „Romanian: a → b (translated again)” and „Icon: a → b”, for each field an edit changed.
function edited(before, after) {
  const fields = fieldsOf(after)
  const lines = fields.filter(([field]) => before?.ro?.[field] !== after.ro?.[field]).map(([field]) =>
    `Romanian${fields.length > 1 ? " " + FIELD_NAMES[field] : ""}: ${shown(before?.ro?.[field])} → ${shown(after.ro?.[field])} (translated again)`)
  if (before?.icon !== after.icon && after.type !== "reason") lines.push(`Icon: ${shown(before?.icon)} → ${shown(after.icon)}`)
  return lines.join(" · ")
}

// A variant corrected by hand or given back to the automatic translation: which language, and its wording before and after.
function translated(before, after) {
  const [language, name] = LANGUAGES.find(([key]) => !same(before?.variants?.[key], after.variants?.[key])) || LANGUAGES[0]
  const fields = fieldsOf(after)
  const wording = variant => fields.map(([field]) => shown(variant?.content?.[field]))
  const was = wording(before?.variants?.[language]), now = wording(after.variants?.[language])
  const parts = fields.map(([field], index) => fields.length > 1 ? `${FIELD_NAMES[field]}: ${was[index]} → ${now[index]}` : `${was[index]} → ${now[index]}`)
    .filter((part, index) => fields.length === 1 || was[index] !== now[index])
  return { name, detail: `${quoted(itemText(after))}: ${parts.join(" · ")}` }
}

// „„X” moved earlier · „Y” moved later”, for the items a reorder moved.
const reordered = items => items.filter(({ before, after }) => before && before.order !== after.order)
  .sort((left, right) => (left.after.order - left.before.order) - (right.after.order - right.before.order))
  .map(({ before, after }) => `${quoted(itemText(after))} moved ${after.order < before.order ? "earlier" : "later"}`).join(" · ")

const counted = (items, verb) => {
  const themes = items.filter(({ after }) => after?.type !== "reason").length
  return `${themes} theme${themes === 1 ? "" : "s"} and ${items.length - themes} reason${items.length - themes === 1 ? "" : "s"} ${verb}.`
}

// `{ actor, title, detail }` of one history entry. `themes` (the catalog read) names the theme of a changed reason.
export function describeChange(entry, themes = []) {
  const items = Array.isArray(entry?.items) ? entry.items.filter(item => item?.after) : []
  const { before, after } = items[0] || {}
  const actor = entry?.actor?.kind === "operator" ? "Launch procedure" : entry?.actor?.name || "SuperAdmin"
  const theme = after?.type === "reason" ? themes.find(candidate => candidate.code === after.themeCode)?.ro?.title || after.themeCode : itemText(after)
  const noun = after?.type === "reason" ? "Reason" : "Theme"
  const which = after?.type === "reason" ? quoted(itemText(after)) + "." : ""
  const describe = () => {
    if (!after) return { title: "Catalog changed", detail: "" }
    switch (entry.action) {
      case "seed": return { title: "Catalog loaded", detail: counted(items, "added") }
      case "createTheme": return { title: `Theme added · ${theme}`, detail: "Inactive until you activate it." }
      case "createReason": return { title: `Reason added · ${theme}`, detail: `${which} Inactive until you activate it.` }
      case "edit": return { title: `${noun} edited · ${theme}`, detail: edited(before, after) }
      case "correctTranslation": {
        const { name, detail } = translated(before, after)
        return { title: `${name} corrected by hand · ${theme}`, detail }
      }
      case "useAutomatic": {
        const { name, detail } = translated(before, after)
        return { title: `${name} back to the automatic translation · ${theme}`, detail }
      }
      case "activate":
        if (items.length > 1) return { title: "Catalog activated", detail: counted(items, "activated") }
        return { title: `${noun} activated · ${theme}`, detail: after.type === "reason" ? which : "Shown in the forms opened from now on." }
      case "deactivate": return { title: `${noun} deactivated · ${theme}`,
        detail: `${after.type === "reason" ? which + " " : ""}Its answers stay in feedback, history and analytics.` }
      case "reorder": return { title: after.type === "reason" ? `Reasons reordered · ${theme}` : "Themes reordered", detail: reordered(items) }
      default: return { title: "Catalog changed", detail: "" }
    }
  }
  return { actor, ...describe() }
}

const WHEN = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Bucharest", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
export const when = iso => {
  const date = iso ? new Date(iso) : null
  return date && !Number.isNaN(date.getTime()) ? WHEN.format(date) : "—"
}
